// Pure data shared by the browser and Edge Functions. Extension is authoritative;
// MIME may be absent or generic, but a specific conflicting MIME is rejected.
export const DOCUMENT_FORMATS = {
  pdf: { mime: ["application/pdf"], group: "PDF", engine: "existing" },
  docx: { mime: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/msword", "application/zip", "application/x-zip-compressed"], group: "Document", engine: "existing" },
  xlsx: { mime: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel", "application/zip", "application/x-zip-compressed"], group: "Spreadsheet", engine: "existing" },
  csv: { mime: ["text/csv", "text/plain", "application/csv", "application/vnd.ms-excel"], group: "Spreadsheet", engine: "existing" },
  txt: { mime: ["text/plain"], group: "Text", engine: "existing" },
  md: { mime: ["text/markdown", "text/x-markdown", "text/plain"], group: "Text", engine: "existing" },
  markdown: { mime: ["text/markdown", "text/x-markdown", "text/plain"], group: "Text", engine: "existing" },
  pptx: { mime: ["application/vnd.openxmlformats-officedocument.presentationml.presentation", "application/zip", "application/x-zip-compressed"], group: "Presentation", engine: "xberg" },
  pages: { mime: ["application/vnd.apple.pages", "application/x-iwork-pages-sffpages", "application/zip"], group: "Document", engine: "xberg" },
  numbers: { mime: ["application/vnd.apple.numbers", "application/x-iwork-numbers-sffnumbers", "application/zip"], group: "Spreadsheet", engine: "xberg" },
  key: { mime: ["application/vnd.apple.keynote", "application/x-iwork-keynote-sffkey", "application/zip"], group: "Presentation", engine: "xberg" },
  json: { mime: ["application/json", "text/json", "text/plain"], group: "Data", engine: "xberg" },
  html: { mime: ["text/html", "text/plain"], group: "Document", engine: "xberg" },
  htm: { mime: ["text/html", "text/plain"], group: "Document", engine: "xberg" },
  tsv: { mime: ["text/tab-separated-values", "text/tsv", "text/plain"], group: "Spreadsheet", engine: "xberg" },
  yaml: { mime: ["application/yaml", "application/x-yaml", "text/yaml", "text/x-yaml", "text/plain"], group: "Data", engine: "xberg" },
  yml: { mime: ["application/yaml", "application/x-yaml", "text/yaml", "text/x-yaml", "text/plain"], group: "Data", engine: "xberg" },
  xml: { mime: ["application/xml", "text/xml", "text/plain"], group: "Data", engine: "xberg" },
  eml: { mime: ["message/rfc822", "application/vnd.ms-outlook", "text/plain"], group: "Email", engine: "xberg" },
  rtf: { mime: ["application/rtf", "text/rtf", "text/plain"], group: "Document", engine: "xberg" },
  odt: { mime: ["application/vnd.oasis.opendocument.text", "application/zip"], group: "Document", engine: "xberg" },
  ods: { mime: ["application/vnd.oasis.opendocument.spreadsheet", "application/zip"], group: "Spreadsheet", engine: "xberg" },
} as const;

export type DocumentKind = keyof typeof DOCUMENT_FORMATS;
export type AttachmentKind = DocumentKind | "image";
const ARCHIVE_DOCUMENTS: ReadonlySet<AttachmentKind> = new Set(["pptx", "pages", "numbers", "key", "odt", "ods"]);

export function requiresArchivePreflight(kind: AttachmentKind): boolean {
  return ARCHIVE_DOCUMENTS.has(kind);
}
const IMAGE_MIME: Record<string, string[]> = {
  png: ["image/png"], jpg: ["image/jpeg"], jpeg: ["image/jpeg"], gif: ["image/gif"], webp: ["image/webp"],
  svg: ["image/svg+xml"], bmp: ["image/bmp", "image/x-ms-bmp"], tif: ["image/tiff"], tiff: ["image/tiff"],
  avif: ["image/avif"], heic: ["image/heic"], heif: ["image/heif"], ico: ["image/x-icon", "image/vnd.microsoft.icon"],
};

export function recognizeDocument(name: string, mime = ""): DocumentKind | null {
  const extension = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] as DocumentKind | undefined;
  if (!extension || !Object.prototype.hasOwnProperty.call(DOCUMENT_FORMATS, extension)) return null;
  const type = mime.toLowerCase().split(";")[0].trim();
  return !type || type === "application/octet-stream" || (DOCUMENT_FORMATS[extension].mime as readonly string[]).includes(type)
    ? extension : null;
}

export function recognizeAttachment(name: string, mime = ""): AttachmentKind | null {
  const document = recognizeDocument(name, mime);
  if (document) return document;
  const extension = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  const allowed = extension && IMAGE_MIME[extension];
  const type = mime.toLowerCase().split(";")[0].trim();
  return allowed && (!type || type === "application/octet-stream" || allowed.includes(type)) ? "image" : null;
}

export function documentAccept(includeImages = false): string {
  const formats = Object.keys(DOCUMENT_FORMATS) as DocumentKind[];
  const extensions = formats.map(kind => `.${kind}`);
  const mimes = formats.flatMap(kind => DOCUMENT_FORMATS[kind].mime).filter(mime =>
    !["application/zip", "application/x-zip-compressed", "application/msword", "application/vnd.ms-excel"].includes(mime));
  return [...extensions, ...new Set(mimes), ...(includeImages ? ["image/*"] : [])].join(",");
}

export function documentGroup(kind: AttachmentKind): string {
  return kind === "image" ? "Image" : DOCUMENT_FORMATS[kind].group;
}

export function usesServerNormalizer(kind: AttachmentKind): boolean {
  return kind !== "image" && DOCUMENT_FORMATS[kind].engine === "xberg";
}
